import { useEffect, useMemo } from "react";
import { useContinuousWorkPlansForUsers, useDerivedWorkingHours, useUserIdsByDateAndRole, useUsersByLocation } from "../../../stores/Calendar/calendar.queries";
import moment from "moment";
import CalendarStore from "../../../stores/Calendar/calendarStore";
import { sortUsersByColumnOrder } from "../../../helpers/calendarColumnOrder";

export const  useSelectedUsers = (locationId:string|undefined, CalendarStore:CalendarStore) => {
    
    // determine the userIds given the location, date, and role mode
      const { data: usersByDate } = useUserIdsByDateAndRole(
        locationId,
        CalendarStore.selectedDateStr,
        CalendarStore.roleForUserList,
        CalendarStore.isRoleMode
      );

      // DEBUG: Log the query parameters and result
      // console.log("[useSelectedUsers] Query state:", {
      //   locationId,
      //   selectedDateStr: CalendarStore.selectedDateStr,
      //   roleForUserList: CalendarStore.roleForUserList,
      //   isRoleMode: CalendarStore.isRoleMode,
      //   usersByDate: usersByDate,
      //   usersByDateCount: usersByDate?.length || 0,
      //   columnUserIds: CalendarStore.columnUserIds,
      //   columnUserIdsCount: CalendarStore.columnUserIds?.length || 0,
      // });
    
      // Compute userIds from role mode or columnUserIds (without side effects during render)
      // In role mode the query result is AUTHORITATIVE once it has resolved — an
      // empty list is a real answer ("nobody holds a work plan at this location on
      // this date"), not a miss to be papered over with the previous date's
      // columns. Falling back on empty left a phantom therapist column standing on
      // every date before the earliest work plan starts.
      const userIds = useMemo(() => {
        if (CalendarStore.isRoleMode) {
          // `undefined` = still loading (or query disabled): keep the current
          // columns so the calendar doesn't blank out between dates.
          if (!usersByDate) return CalendarStore.columnUserIds ?? [];
          // Kept in column order so the ids line up with the rendered columns.
          return sortUsersByColumnOrder(usersByDate)
            .map((u) => u.id)
            .filter(Boolean) as string[];
        }
        if (CalendarStore.columnUserIds && CalendarStore.columnUserIds.length > 0) {
          return CalendarStore.columnUserIds;
        }
        return [];
      }, [CalendarStore.isRoleMode, usersByDate, CalendarStore.columnUserIds]);

      // DEBUG: Log computed userIds
      // console.log("[useSelectedUsers] Computed userIds:", {
        // userIds,
        // userIdsCount: userIds.length,
        // reason: CalendarStore.isRoleMode && usersByDate && usersByDate.length > 0 
          // ? "from role mode" 
          // : CalendarStore.columnUserIds && CalendarStore.columnUserIds.length > 0 
            // ? "from columnUserIds" 
            // : "empty (no valid source)"
      // });
    
      // Sync columnUserIds in store when role mode produces different user list
      // This must be in useEffect to avoid setState during render.
      // Same rule as above: sync on any RESOLVED result, including an empty one —
      // otherwise columnUserIds keeps the last non-empty list forever, and the
      // resources/`selectedUserId` derived from it keep a therapist on screen for
      // dates they never worked.
      useEffect(() => {
        if (CalendarStore.isRoleMode && usersByDate) {
          const roleUserIds = sortUsersByColumnOrder(usersByDate)
            .map((u) => u.id)
            .filter(Boolean) as string[];
          // Compared as sets (both sides sorted by id), so storing them in
          // column order does not re-trigger this effect.
          const sortedRoleIds = JSON.stringify(roleUserIds.slice().sort());
          const sortedColumnIds = JSON.stringify(CalendarStore.columnUserIds.slice().sort());
          if (sortedRoleIds !== sortedColumnIds) {
            CalendarStore.setColumnUserIds(roleUserIds);
          }
        }
      }, [CalendarStore.isRoleMode, usersByDate, CalendarStore.columnUserIds, CalendarStore]);
    
      const { data: unorderedUsers = [] } = useUsersByLocation(
        locationId,
        CalendarStore.columnUserIds && CalendarStore.columnUserIds.length > 0 ? ({ userIds: CalendarStore.columnUserIds } as any) : undefined,
        false,
        !!locationId
      );

      // Column order is applied once, here: the controllers build their
      // resources by filtering this list and preserve its order, so both the
      // main/continuous calendar and the work hour view stay in sync.
      const fetchedUsers = useMemo(
        () => sortUsersByColumnOrder(unorderedUsers),
        [unorderedUsers]
      );

    // When a specific user is selected (week view), fetch working hours for the whole week
    // Otherwise (daily view with all users), fetch only for the selected date
    const hasUserFilter = CalendarStore.columnUserIds && CalendarStore.columnUserIds.length > 0;
    const weekStartDate = moment(CalendarStore.selectedDateStr).startOf('isoWeek').format('YYYY-MM-DD');
    const weekEndDate = moment(CalendarStore.selectedDateStr).endOf('isoWeek').format('YYYY-MM-DD');
    
    // When no user filter is active, use all fetched users' IDs for working hours
    const workingHoursUserIds = hasUserFilter 
      ? userIds 
      : fetchedUsers.map(u => u.id).filter(Boolean) as string[];
    
    const { data: derivedWorkingHours } = useDerivedWorkingHours(
      workingHoursUserIds,
      hasUserFilter ? weekStartDate : CalendarStore.selectedDateStr,
      hasUserFilter ? weekEndDate : CalendarStore.selectedDateStr,
      true,
      // Work plans are location-specific: the planner calendar must only show
      // each user's plan at the location currently displayed.
      locationId
    );
    
    // Calculate dayOfWeek from the selected date (always use selectedDateStr, not weekStartDate)
    // When a user filter is active (week view with single therapist), pass undefined to fetch ALL days
    const dayOfWeek = hasUserFilter ? undefined : moment(CalendarStore.selectedDateStr).isoWeekday();
    // Include continuousView in dependencies to refetch when view mode changes
    const { data: continuousWorkPlansForUsers } = useContinuousWorkPlansForUsers(
      workingHoursUserIds,
      dayOfWeek,
      hasUserFilter ? weekStartDate : CalendarStore.selectedDateStr,
      true,
      locationId
    );
    
    return { userIds, fetchedUsers, derivedWorkingHours, continuousWorkPlansForUsers };
  }
